import { AssessmentDomainStatus, FileStatus } from '@libs/constants';
import { PluralKeys, SingularKeys } from '@libs/i18n';
import { AssessmentDetail } from '@libs/types';
import { hasValidClassification, needsClassificationDecision } from '@libs/utils';

import { AssessmentDocumentViewModel } from '../types';

import { findDomainByOrderIndex } from './assessment-mapper';

interface ValidateDomainBeforeSaveParams {
  domainStatus: string | undefined;
  isApplicable: boolean;
  noEvidenceChecked: boolean;
  files: AssessmentDocumentViewModel[];
  minRequiredDocuments: number;
  maxRequiredDocuments: number;
}

type DomainSaveValidationResult =
  | { valid: true }
  | { valid: false; errorKey: SingularKeys | PluralKeys; errorParams?: Record<string, unknown> };

/**
 * Validates whether the current domain can be saved.
 *
 * Only runs when the domain is IN_PROGRESS, applicable, and not skipped.
 * Returns a translation key + optional params so the caller can display
 * the localised message however it sees fit (inline error, snackbar, etc.).
 *
 * Document counting rules:
 * - Only UPLOADED / NOT_SAVED files are counted as "active"
 * - Of those, only files with medium/high classification confidence count
 *   toward the min/max requirement; unclassified or low-confidence files
 *   are treated as missing and trigger a separate error.
 *
 * @returns `{ valid: true }` if saving can proceed, or `{ valid: false, errorKey, errorParams }` otherwise
 */
export const validateDomainBeforeSave = ({
  domainStatus,
  isApplicable,
  noEvidenceChecked,
  files,
  minRequiredDocuments,
  maxRequiredDocuments,
}: ValidateDomainBeforeSaveParams): DomainSaveValidationResult => {
  // Validation only applies when the domain is actively being edited
  if (domainStatus !== AssessmentDomainStatus.IN_PROGRESS || !isApplicable || noEvidenceChecked) {
    return { valid: true };
  }

  const hasUploadingFiles = files.some(
    (f) => f.status === FileStatus.QUEUED || f.status === FileStatus.UPLOADING,
  );
  if (hasUploadingFiles) {
    return { valid: false, errorKey: 'app.assessment.workflow.validation.waitForUploads' };
  }

  const activeFiles = files.filter(
    (f) => f.status === FileStatus.UPLOADED || f.status === FileStatus.NOT_SAVED,
  );

  if (activeFiles.length === 0) {
    return { valid: false, errorKey: 'app.assessment.workflow.validation.noFilesOrNoEvidence' };
  }

  // Check that at least one active document has a valid classification (medium/high confidence)
  const validFiles = activeFiles.filter((f) => hasValidClassification({ classificationMetadata: f.classificationMetadata ?? null }));
  if (validFiles.length === 0) {
    return {
      valid: false,
      errorKey: 'app.assessment.workflow.validation.noValidClassification',
    };
  }

  // A weakly-matched document is graded against whichever expected document the
  // AI guessed at, so leaving the domain without answering it turns a guess into
  // a score. It counts toward the domain either way, which is why it has to be
  // an explicit decision rather than a warning the user can walk past.
  const undecided = activeFiles.filter((f) =>
    f.classificationMetadata ? needsClassificationDecision(f.classificationMetadata) : false,
  );
  if (undecided.length > 0) {
    return {
      valid: false,
      errorKey: 'app.assessment.workflow.validation.undecidedClassification',
      // `count` is the number of *other* files, so one undecided file reads as
      // "…needs you to confirm" rather than claiming a second one exists.
      errorParams: { count: undecided.length - 1, name: undecided[0].fileName },
    };
  }

  // Check that no two documents share the same matched standard document key
  const seenKeys = new Map<string, string>();
  for (const f of activeFiles) {
    const key = f.classificationMetadata?.matchedStandardDocumentKey;
    if (!key) continue;
    if (seenKeys.has(key)) {
      return {
        valid: false,
        errorKey: 'app.assessment.workflow.validation.duplicateClassification',
        errorParams: { name: f.classificationMetadata?.matchedStandardDocumentName || key },
      };
    }
    seenKeys.set(key, f.fileName);
  }

  const validFilesCount = validFiles.length;

  if (minRequiredDocuments > 0 && validFilesCount < minRequiredDocuments) {
    return {
      valid: false,
      errorKey: 'app.assessment.workflow.validation.minFiles',
      errorParams: { count: minRequiredDocuments },
    };
  }

  if (validFilesCount > maxRequiredDocuments) {
    return {
      valid: false,
      errorKey: 'app.assessment.workflow.validation.maxFiles',
      errorParams: { count: maxRequiredDocuments },
    };
  }

  return { valid: true };
};

/**
 * Assessment Workflow Validation Utilities
 * 
 * This module provides all validation logic for the assessment workflow:
 * - Unsaved changes detection
 * - Domain navigation permissions
 * - Save requirements validation
 * - Domain index validation
 * 
 * @see {@link CLAUDE.md} - Assessment workflow architecture and patterns
 */

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates if domain has unsaved changes
 * 
 * Checks for:
 * - Files currently being uploaded (optimistic UI state)
 * - Documents with 'pending' status (uploaded but not confirmed via save)
 * - Documents with 'deleted' flag (marked for deletion, not yet persisted)
 * 
 * Future extensions:
 * - Field value changes (when field editing is implemented)
 * - Unsaved field configurations
 * - Domain-specific state changes
 * 
 * @param uploadingFilesCount - Number of files currently in upload state
 * @param documents - Array of domain documents from API
 * @returns true if domain has unsaved changes that require save operation
 */
export const hasUnsavedChanges = (
  files: Array<AssessmentDocumentViewModel>,
): boolean => {
  const hasUploadingFiles = files.some((file) => file.status === FileStatus.UPLOADING);
  const hasPendingDocuments = files.some((file) => file.status === FileStatus.NOT_SAVED);
  const hasDeletedDocuments = files.some((file) => file.status === FileStatus.DELETED);

  return hasUploadingFiles || hasPendingDocuments || hasDeletedDocuments;
};

/**
 * Validates if domain needs to be saved before proceeding
 * 
 * A domain needs to be saved if:
 * 1. Domain is not yet completed (status !== COMPLETED), OR
 * 2. Domain has unsaved changes (uploads, pending docs, deleted docs)
 * 
 * This prevents saving when:
 * - Domain is already COMPLETED
 * - AND no new changes have been made
 * 
 * @param domainStatus - Current status of the domain
 * @param uploadingFilesCount - Number of files currently in upload state
 * @param documents - Array of domain documents from API
 * @returns true if save operation is required before navigation
 */
export const needsToSave = (
  files: Array<AssessmentDocumentViewModel>,
): boolean => {

  // Save if domain is not completed OR has unsaved changes
  return hasUnsavedChanges(files);
};

/**
 * Validates if navigation to target domain is permitted
 * 
 * Current rules:
 * - COMPLETED domains: Allowed (can review/edit)
 * - IN_PROGRESS domains: Allowed (current working domain)
 * - NOT_STARTED domains: Not allowed (must follow sequential workflow)
 * 
 * Future enhancements:
 * - User permission checks (role-based access)
 * - Prerequisite domain completion checks
 * - Domain lock status (collaborative editing)
 * - Time-based access restrictions
 * 
 * @param domainState - The status of the target domain
 * @returns true if user can navigate to this domain
 */
export const canNavigateToDomain = (
  domainState: AssessmentDomainStatus | undefined,
): boolean => {
  return (
    domainState === AssessmentDomainStatus.COMPLETED ||
    domainState === AssessmentDomainStatus.IN_PROGRESS
  );
};

/**
 * Validates if next domain exists and returns its ID
 * 
 * Validation steps:
 * 1. Checks if assessment data is loaded
 * 2. Verifies we're not already on the last domain
 * 3. Calculates next domain orderIndex (currentOrderIndex + 1)
 * 4. Resolves domain ID from orderIndex
 * 
 * @param currentOrderIndex - Current domain orderIndex (1-based from API)
 * @param assessment - Full assessment data with domain array
 * @param totalDomains - Total number of domains in assessment
 * @returns Validation result with next domain ID and orderIndex, or error message
 */
export const validateNextDomain = (
  currentOrderIndex: number,
  assessment: AssessmentDetail | undefined,
  totalDomains: number,
): { isValid: boolean; nextDomainId?: string; nextIndex?: number; error?: string } => {
  if (!assessment) {
    return { isValid: false, error: 'Assessment data not loaded' };
  }

  if (currentOrderIndex >= totalDomains) {
    return { isValid: false, error: 'Already on last domain' };
  }

  const nextIndex = currentOrderIndex + 1;
  const nextDomain = findDomainByOrderIndex(assessment, nextIndex);
  const nextDomainId = nextDomain?.id;

  if (!nextDomainId) {
    return { isValid: false, error: `Domain not found for orderIndex ${nextIndex}` };
  }

  return { isValid: true, nextDomainId, nextIndex };
};

/**
 * Validates if previous domain exists and returns its ID
 * 
 * Validation steps:
 * 1. Checks if assessment data is loaded
 * 2. Verifies we're not already on the first domain
 * 3. Calculates previous domain orderIndex (currentOrderIndex - 1)
 * 4. Resolves domain ID from orderIndex
 * 
 * @param currentOrderIndex - Current domain orderIndex (1-based from API)
 * @param assessment - Full assessment data with domain array
 * @returns Validation result with previous domain ID and orderIndex, or error message
 */
export const validatePreviousDomain = (
  currentOrderIndex: number,
  assessment: AssessmentDetail | undefined,
): { isValid: boolean; prevDomainId?: string; prevIndex?: number; error?: string } => {
  if (!assessment) {
    return { isValid: false, error: 'Assessment data not loaded' };
  }

  if (currentOrderIndex <= 1) {
    return { isValid: false, error: 'Already on first domain' };
  }

  const prevIndex = currentOrderIndex - 1;
  const prevDomain = findDomainByOrderIndex(assessment, prevIndex);
  const prevDomainId = prevDomain?.id;

  if (!prevDomainId) {
    return { isValid: false, error: `Domain not found for orderIndex ${prevIndex}` };
  }

  return { isValid: true, prevDomainId, prevIndex };
};
